import * as React from "react";
import { cn } from "../ui/index.ts";
import { I } from "../icons/icons.tsx";
import { useDragCtx } from "../docking/docking.component.tsx";

export interface GridColumn<T> {
  id: string;
  label: string;
  sortable?: boolean;
  accessor: (row: T) => string | number | boolean | null | undefined;
  render?: (row: T) => React.ReactNode;
  defaultVisible?: boolean;
  width?: string;
  align?: "left" | "right" | "center";
}

interface SortState {
  columnId: string;
  dir: "asc" | "desc";
}

interface PersistedConfig {
  v: 1;
  order: string[];
  hidden: string[];
  sort: SortState | null;
  widths?: Record<string, number>;
}

const STORAGE_PREFIX = "orthonexus-grid:";
const CONFIG_VERSION = 1;
const MIN_COL_WIDTH = 40;

function loadConfig(instanceId: string): PersistedConfig | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + instanceId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedConfig;
    if (parsed?.v !== CONFIG_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConfig(instanceId: string, cfg: PersistedConfig): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_PREFIX + instanceId, JSON.stringify(cfg));
  } catch {
    /* non-fatal */
  }
}

export function AdvancedGrid<T>({
  instanceId,
  columns,
  rows,
  rowKey,
  emptyMessage = "Aucun élément.",
  onRowClick,
  toolbar,
}: {
  instanceId: string;
  columns: GridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  toolbar?: React.ReactNode;
}) {
  const { editMode } = useDragCtx();
  const defaultOrder = React.useMemo(() => columns.map((c) => c.id), [columns]);
  const defaultHidden = React.useMemo(
    () => columns.filter((c) => c.defaultVisible === false).map((c) => c.id),
    [columns],
  );

  const [order, setOrder] = React.useState<string[]>(() => {
    const stored = loadConfig(instanceId);
    if (!stored) return defaultOrder;
    const known = new Set(defaultOrder);
    const fromStored = stored.order.filter((id) => known.has(id));
    const missing = defaultOrder.filter((id) => !fromStored.includes(id));
    return [...fromStored, ...missing];
  });

  const [hidden, setHidden] = React.useState<string[]>(
    () => loadConfig(instanceId)?.hidden ?? defaultHidden,
  );

  const [sort, setSort] = React.useState<SortState | null>(
    () => loadConfig(instanceId)?.sort ?? null,
  );

  const [widths, setWidths] = React.useState<Record<string, number>>(
    () => loadConfig(instanceId)?.widths ?? {},
  );

  React.useEffect(() => {
    saveConfig(instanceId, {
      v: CONFIG_VERSION,
      order,
      hidden,
      sort,
      widths,
    });
  }, [instanceId, order, hidden, sort, widths]);

  const colMap = React.useMemo(() => {
    const m = new Map<string, GridColumn<T>>();
    for (const c of columns) m.set(c.id, c);
    return m;
  }, [columns]);

  const orderedVisibleColumns = React.useMemo(() => {
    return order
      .filter((id) => !hidden.includes(id))
      .map((id) => colMap.get(id))
      .filter((c): c is GridColumn<T> => Boolean(c));
  }, [order, hidden, colMap]);

  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    const col = colMap.get(sort.columnId);
    if (!col) return rows;
    const copy = rows.slice();
    copy.sort((a, b) => {
      const va = col.accessor(a);
      const vb = col.accessor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), "fr", { numeric: true });
    });
    if (sort.dir === "desc") copy.reverse();
    return copy;
  }, [rows, sort, colMap]);

  const onSortClick = (col: GridColumn<T>) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.columnId !== col.id) {
        return { columnId: col.id, dir: "asc" };
      }
      if (prev.dir === "asc") return { columnId: col.id, dir: "desc" };
      return null;
    });
  };

  const gridTemplate = orderedVisibleColumns
    .map((c) => {
      const w = widths[c.id];
      if (typeof w === "number") return `${w}px`;
      if (c.width) return c.width;
      return "minmax(80px, 1fr)";
    })
    .join(" ");

  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Drag-to-reorder via column headers
  const [dragColId, setDragColId] = React.useState<string | null>(null);
  const draggedRef = React.useRef(false);

  const onHeaderDragStart = (id: string) => {
    setDragColId(id);
    draggedRef.current = true;
  };
  const onHeaderDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!dragColId || dragColId === overId) return;
    const from = order.indexOf(dragColId);
    const to = order.indexOf(overId);
    if (from < 0 || to < 0) return;
    const next = order.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setOrder(next);
  };
  const onHeaderDragEnd = () => {
    setDragColId(null);
    // Defer click cancellation past any synthetic click the browser might fire
    setTimeout(() => {
      draggedRef.current = false;
    }, 0);
  };

  // Column resize
  const startResize = (colId: string, startX: number, startWidth: number) => {
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;
      const next = Math.max(MIN_COL_WIDTH, Math.round(startWidth + delta));
      setWidths((prev) => ({ ...prev, [colId]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const reset = () => {
    setOrder(defaultOrder);
    setHidden(defaultHidden);
    setSort(null);
    setWidths({});
  };

  const showHeaderBar = Boolean(toolbar) || editMode;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showHeaderBar && (
        <div className="flex items-center justify-between gap-2 px-2 pt-1.5 pb-1">
          <div className="min-w-0 flex-1">{toolbar}</div>
          {editMode && (
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              title="Configurer les colonnes"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
            >
              <I.Settings size={13} />
            </button>
          )}
        </div>
      )}

      {editMode && pickerOpen && (
        <div className="px-2 pb-1.5">
          <ColumnPicker
            columns={columns}
            order={order}
            hidden={hidden}
            onOrderChange={setOrder}
            onHiddenChange={setHidden}
            onReset={reset}
            onClose={() => setPickerOpen(false)}
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {orderedVisibleColumns.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-sf-text-muted">
            Aucune colonne visible. Ouvrez la configuration pour en activer.
          </div>
        ) : (
          <div className="min-w-fit">
            <div
              className="sticky top-0 z-10 grid border-b border-sf-border bg-sf-surface text-[11px] font-semibold uppercase tracking-wide text-sf-text-muted"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {orderedVisibleColumns.map((c) => {
                const isSorted = sort?.columnId === c.id;
                const align = c.align ?? "left";
                const isDragged = dragColId === c.id;
                return (
                  <div
                    key={c.id}
                    draggable={editMode}
                    onDragStart={
                      editMode
                        ? (e) => {
                            e.dataTransfer.effectAllowed = "move";
                            onHeaderDragStart(c.id);
                          }
                        : undefined
                    }
                    onDragOver={
                      editMode ? (e) => onHeaderDragOver(e, c.id) : undefined
                    }
                    onDragEnd={editMode ? onHeaderDragEnd : undefined}
                    onClick={
                      editMode
                        ? () => {
                            if (draggedRef.current) return;
                            onSortClick(c);
                          }
                        : undefined
                    }
                    className={cn(
                      "relative flex items-center gap-1 truncate border-r border-sf-border/40 px-2 py-1.5 last:border-r-0",
                      align === "right" && "justify-end",
                      align === "center" && "justify-center",
                      editMode &&
                        c.sortable &&
                        "cursor-pointer hover:text-sf-text",
                      editMode && !c.sortable && "cursor-grab",
                      isDragged && "opacity-50",
                    )}
                  >
                    <span className="truncate">{c.label}</span>
                    {c.sortable && isSorted && (
                      <I.ChevronDown
                        size={11}
                        className={cn(
                          "shrink-0 transition-transform",
                          sort?.dir === "asc" && "rotate-180",
                        )}
                      />
                    )}
                    {editMode && (
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        title="Glisser pour redimensionner"
                        onDragStart={(e) => e.preventDefault()}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const parent = e.currentTarget
                            .parentElement as HTMLElement;
                          const startWidth =
                            parent.getBoundingClientRect().width;
                          startResize(c.id, e.clientX, startWidth);
                        }}
                        className="absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-sf-primary/40"
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {sortedRows.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-sf-text-muted">
                {emptyMessage}
              </div>
            ) : (
              sortedRows.map((row) => (
                <div
                  key={rowKey(row)}
                  role={onRowClick ? "button" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    "grid border-b border-sf-border/60 text-[12.5px] text-sf-text",
                    onRowClick &&
                      "cursor-pointer hover:bg-sf-surface-alt focus:bg-sf-surface-alt focus:outline-none",
                  )}
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  {orderedVisibleColumns.map((c) => {
                    const align = c.align ?? "left";
                    return (
                      <div
                        key={c.id}
                        className={cn(
                          "min-w-0 truncate px-2 py-1.5",
                          align === "right" && "text-right",
                          align === "center" && "text-center",
                        )}
                      >
                        {c.render
                          ? c.render(row)
                          : String(c.accessor(row) ?? "")}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ColumnPicker<T>({
  columns,
  order,
  hidden,
  onOrderChange,
  onHiddenChange,
  onReset,
  onClose,
}: {
  columns: GridColumn<T>[];
  order: string[];
  hidden: string[];
  onOrderChange: (next: string[]) => void;
  onHiddenChange: (next: string[]) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const colMap = React.useMemo(() => {
    const m = new Map<string, GridColumn<T>>();
    for (const c of columns) m.set(c.id, c);
    return m;
  }, [columns]);

  const [dragId, setDragId] = React.useState<string | null>(null);

  const move = (id: string, dir: -1 | 1) => {
    const idx = order.indexOf(id);
    if (idx < 0) return;
    const next = order.slice();
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onOrderChange(next);
  };

  const toggleVisible = (id: string) => {
    if (hidden.includes(id)) {
      onHiddenChange(hidden.filter((h) => h !== id));
    } else {
      onHiddenChange([...hidden, id]);
    }
  };

  const onDragStart = (id: string) => setDragId(id);
  const onDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    const from = order.indexOf(dragId);
    const to = order.indexOf(overId);
    if (from < 0 || to < 0) return;
    const next = order.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onOrderChange(next);
  };

  return (
    <div className="rounded-md border border-sf-border bg-white p-2 shadow-sm">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-sf-text-muted">
          Colonnes
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onReset}
            className="rounded border border-sf-border bg-white px-1.5 py-0.5 text-[11px] text-sf-text-muted hover:bg-sf-surface-alt"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-sf-border bg-white px-1.5 py-0.5 text-[11px] text-sf-text-muted hover:bg-sf-surface-alt"
          >
            Fermer
          </button>
        </div>
      </div>
      <ul className="flex flex-col gap-px">
        {order.map((id, idx) => {
          const col = colMap.get(id);
          if (!col) return null;
          const visible = !hidden.includes(id);
          return (
            <li
              key={id}
              draggable
              onDragStart={() => onDragStart(id)}
              onDragOver={(e) => onDragOver(e, id)}
              onDragEnd={() => setDragId(null)}
              className={cn(
                "flex items-center gap-2 rounded px-1.5 py-1 text-[12.5px]",
                dragId === id ? "bg-sf-primary-50" : "hover:bg-sf-surface-alt",
              )}
            >
              <span
                className="cursor-grab select-none text-sf-text-muted"
                title="Glisser pour réordonner"
              >
                <I.Drag size={12} />
              </span>
              <input
                type="checkbox"
                checked={visible}
                onChange={() => toggleVisible(id)}
                className="size-3.5 accent-sf-primary"
              />
              <span className="min-w-0 flex-1 truncate">{col.label}</span>
              <button
                type="button"
                onClick={() => move(id, -1)}
                disabled={idx === 0}
                title="Monter"
                className="rounded px-1 text-sf-text-muted hover:bg-white disabled:opacity-30"
              >
                <I.ChevronUp size={11} />
              </button>
              <button
                type="button"
                onClick={() => move(id, 1)}
                disabled={idx === order.length - 1}
                title="Descendre"
                className="rounded px-1 text-sf-text-muted hover:bg-white disabled:opacity-30"
              >
                <I.ChevronDown size={11} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
